export type Language = "en" | "ru";

export const uiText = {
  en: {
    locale: "en-US",
    documentTitle: "p2p-netcat web — P2P terminal in your browser",
    documentDescription: "An encrypted browser P2P terminal that connects by PeerId",
    socialImageAlt: "p2p-netcat web — a terminal between two peers",
    stateLabels: {
      idle: "Ready",
      starting: "Starting node",
      connecting: "Connecting",
      connected: "Online",
      reconnecting: "Reconnecting",
      closed: "Closed",
      error: "Error",
    },
    byteUnits: ["B", "KB", "MB"],
    ptyEnabled: "PTY protocol enabled; keyboard input is sent directly",
    connectionClosed: "Connection closed",
    ptyEofSent: "PTY EOF sent; waiting for the remote shell to exit",
    sendingFile: "Sending",
    fileSentSuffix: "sent",
    fileSentPrefix: "File",
    copyFailed: "Could not copy the command",
    brandAria: "p2p netcat — top of page",
    languageLink: "Русская версия",
    languageLinkAria: "Open the Russian version",
    eyebrow: "Encrypted stream · no server IP address",
    heroLineOne: "A terminal between two peers.",
    heroLineTwo: "Right in your browser.",
    heroCopy:
      "Enter a PeerId and the client will discover a browser-compatible route automatically. You can provide a Circuit Relay manually only as a fallback.",
    projectBadgesAria: "Project status and resources",
    installTitle: "Install the command-line utility",
    copied: "Copied",
    copy: "Copy",
    installationNavAria: "Installation and presentation resources",
    installationGuide: "Installation guide ↗",
    alternateInstallationGuide: "Русская инструкция ↗",
    presentationGuide: "Product & technical deck (PDF) ↗",
    presentationMobileGuide: "Mobile PDF ↗",
    presentationSourceGuide: "PPTX source ↓",
    workspaceAria: "P2P client",
    route: "Route",
    newConnection: "New connection",
    serverPeerId: "Server PeerId",
    peerHelp: "Printed by the command",
    additionalRelay: "Additional relay",
    advancedConnection: "Private access and relay",
    pairingToken: "Pairing token",
    pairingTokenHelp: "Optional for libp2p · required for native WebRTC; fills PeerId and port and hides public discovery keys",
    privateDiscovery: "Private rotating discovery",
    manualRoute: "Route specified manually",
    automaticDiscovery: "Optional · automatic discovery is enabled",
    relayPlaceholder: "Leave empty for automatic discovery",
    relayHelp: "If automatic discovery fails:",
    logicalPort: "Logical port",
    timeout: "Connection timeout (peer discovery)",
    interactivePty: "Interactive PTY",
    interactiveHelp: "Enable this when the server runs",
    connect: "Connect",
    disconnect: "Disconnect",
    browserPeerId: "Browser PeerId",
    peerIdOnConnect: "Created when you connect",
    endToEndEncryption: "End-to-end encryption",
    securityNote:
      "Discovery services can see the PeerId query, but the channel contents are protected by Noise.",
    pairingSecurityNote:
      "The token enables private rotating discovery, encrypted signaling, and mutual admission authentication.",
    ptyExit: "PTY · Ctrl-E Q — exit",
    showSentText: "Show sent text",
    terminalLoading: "Loading PTY terminal…",
    terminalEmpty: "Waiting for data…\nRemote process output will appear here after the connection opens.",
    sendDataAria: "Data to send",
    send: "Send",
    sendFile: "Send file",
    sendEof: "Send EOF",
    downloadReceived: "Download received data",
    ptyHelp: "Click the terminal and type; Enter and control keys are sent directly",
    connectionLogAria: "Connection log",
    eventLog: "Event log",
    clear: "Clear",
    noEvents: "No events yet.",
    terminalAria: "Interactive remote PTY terminal",
  },
  ru: {
    locale: "ru-RU",
    documentTitle: "p2p-netcat web — P2P-терминал в браузере",
    documentDescription: "Зашифрованный P2P-терминал в браузере с подключением по PeerId",
    socialImageAlt: "p2p-netcat web — терминал между двумя узлами",
    stateLabels: {
      idle: "Готов",
      starting: "Запуск узла",
      connecting: "Соединение",
      connected: "В сети",
      reconnecting: "Переподключение",
      closed: "Закрыто",
      error: "Ошибка",
    },
    byteUnits: ["Б", "КБ", "МБ"],
    ptyEnabled: "PTY-протокол включён; ввод передаётся напрямую с клавиатуры",
    connectionClosed: "Соединение закрыто",
    ptyEofSent: "PTY EOF отправлен; ожидаем завершение удалённой оболочки",
    sendingFile: "Отправка",
    fileSentSuffix: "отправлен",
    fileSentPrefix: "Файл",
    copyFailed: "Не удалось скопировать команду",
    brandAria: "p2p netcat — начало страницы",
    languageLink: "English version",
    languageLinkAria: "Open the English version",
    eyebrow: "Зашифрованный поток · без IP-адреса сервера",
    heroLineOne: "Терминал между двумя узлами.",
    heroLineTwo: "Прямо из браузера.",
    heroCopy:
      "Введите PeerId — клиент сам найдёт браузерный маршрут через IPFS. Circuit Relay можно указать вручную только как резервный маршрут.",
    projectBadgesAria: "Статус и ресурсы проекта",
    installTitle: "Установить консольную утилиту",
    copied: "Скопировано",
    copy: "Копировать",
    installationNavAria: "Установка и презентация проекта",
    installationGuide: "Инструкция по установке ↗",
    alternateInstallationGuide: "English ↗",
    presentationGuide: "Презентация о продукте и архитектуре (PDF) ↗",
    presentationMobileGuide: "Мобильный PDF ↗",
    presentationSourceGuide: "Исходник PPTX ↓",
    workspaceAria: "P2P-клиент",
    route: "Маршрут",
    newConnection: "Новое соединение",
    serverPeerId: "PeerId сервера",
    peerHelp: "Значение печатает команда",
    additionalRelay: "Дополнительный relay",
    advancedConnection: "Приватный доступ и relay",
    pairingToken: "Pairing token",
    pairingTokenHelp: "Необязательно для libp2p · обязательно для native WebRTC; заполняет PeerId и порт и скрывает публичные ключи поиска",
    privateDiscovery: "Приватный вращающийся поиск",
    manualRoute: "Маршрут задан вручную",
    automaticDiscovery: "Необязательно · используется автопоиск",
    relayPlaceholder: "Оставьте пустым для автоматического поиска",
    relayHelp: "Если автопоиск не сработал:",
    logicalPort: "Логический порт",
    timeout: "Таймаут подключения (поиск узла)",
    interactivePty: "Интерактивный PTY",
    interactiveHelp: "Включите, если сервер запущен командой",
    connect: "Подключиться",
    disconnect: "Отключиться",
    browserPeerId: "Браузерный PeerId",
    peerIdOnConnect: "Будет создан при подключении",
    endToEndEncryption: "Сквозное шифрование",
    securityNote:
      "Службы поиска видят запрос PeerId, но содержимое канала защищено Noise.",
    pairingSecurityNote:
      "Token включает приватный вращающийся поиск, зашифрованный signaling и взаимную проверку допуска.",
    ptyExit: "PTY · Ctrl-E Q — выход",
    showSentText: "Показывать отправленное",
    terminalLoading: "Загрузка PTY-терминала…",
    terminalEmpty: "Ожидание данных…\nПосле соединения вывод удалённого процесса появится здесь.",
    sendDataAria: "Данные для отправки",
    send: "Отправить",
    sendFile: "Отправить файл",
    sendEof: "Отправить EOF",
    downloadReceived: "Скачать приём",
    ptyHelp: "Кликните терминал и печатайте; Enter и управляющие клавиши передаются напрямую",
    connectionLogAria: "Журнал соединения",
    eventLog: "Журнал событий",
    clear: "Очистить",
    noEvents: "Событий пока нет.",
    terminalAria: "Интерактивный терминал удалённого PTY",
  },
} as const;

export function getPageLanguage(): Language {
  if (typeof window === "undefined") return "en";
  return new URLSearchParams(window.location.search).get("lang") === "ru" ? "ru" : "en";
}

export function getLanguageUrl(language: Language) {
  if (typeof window === "undefined") return language === "ru" ? "?lang=ru" : ".";
  const url = new URL(window.location.href);
  if (language === "ru") url.searchParams.set("lang", "ru");
  else url.searchParams.delete("lang");
  return `${url.pathname}${url.search}${url.hash}`;
}

const englishDiagnosticPatterns: ReadonlyArray<readonly [RegExp, string]> = [
  [
    /WebRTC: найден кандидат (.+), проверяем PeerId/,
    "WebRTC: found candidate $1; verifying PeerId",
  ],
  [
    /WebRTC peer предъявил другой PeerId: (.+)/,
    "The WebRTC peer presented a different PeerId: $1",
  ],
  [
    /WebRTC: PeerId (.+) подтверждён/,
    "WebRTC: PeerId $1 verified",
  ],
  [
    /WebRTC не нашёл (.+) за (\d+) с/,
    "WebRTC did not find $1 within $2 s",
  ],
  [
    /WebRTC-канал временно потерян; сохраняем PTY и ждём переподключение до (\d+) с/,
    "WebRTC channel temporarily lost; preserving the PTY and waiting up to $1 s for reconnection",
  ],
  [
    /WebRTC-канал восстановлен через (.+)/,
    "WebRTC channel restored through $1",
  ],
  [
    /Сетевой конфиг недоступен, используем встроенный: (.+)/,
    "Network config unavailable; using the built-in config: $1",
  ],
  [
    /DHT не вернула браузерный адрес: (.+)/,
    "The DHT did not return a browser-compatible address: $1",
  ],
  [
    /Браузерный PeerId: (.+)/,
    "Browser PeerId: $1",
  ],
  [
    /Используем указанный relay для (.+)/,
    "Using the specified relay for $1",
  ],
  [
    /Ищем (.+) через подписанный PubSub, delegated routing и IPFS DHT/,
    "Searching for $1 through signed PubSub, delegated routing, and the IPFS DHT",
  ],
  [
    /Пропущен некорректный relay из network-config\.json: (.+)/,
    "Skipped an invalid relay from network-config.json: $1",
  ],
  [
    /Найдено браузерных маршрутов: (\d+); выбираем самый быстрый/,
    "Found $1 browser-compatible routes; selecting the fastest",
  ],
  [
    /Канал (.+) открыт/,
    "Channel $1 opened",
  ],
  [
    /libp2p не установил соединение за (\d+) с/,
    "libp2p did not establish a connection within $1 s",
  ],
  [
    /Логический порт должен быть целым числом от 1 до 65535, получено: (.+)/,
    "The logical port must be an integer from 1 to 65535; received: $1",
  ],
  [
    /Адрес relay должен содержать \/p2p\/PeerId: (.+)/,
    "The relay address must contain /p2p/PeerId: $1",
  ],
];

const englishDiagnosticFragments: ReadonlyArray<readonly [string, string]> = [
  ["Web Crypto API недоступен.", "Web Crypto API is unavailable."],
  ["Откройте приложение по HTTPS, а не по HTTP.", "Open the application over HTTPS, not HTTP."],
  ["IndexedDB недоступна", "IndexedDB is unavailable"],
  ["Не удалось прочитать кеш маршрутов", "Could not read the route cache"],
  ["Не удалось записать кеш маршрутов", "Could not write the route cache"],
  ["Нет доступных браузеру маршрутов", "No browser-compatible routes are available"],
  ["Выбран более быстрый маршрут", "A faster route was selected"],
  ["Все найденные браузерные маршруты отклонены", "All discovered browser-compatible routes were rejected"],
  ["Сетевой стек запускается в Web Worker", "Starting the network stack in a Web Worker"],
  ["Соединение уже открыто", "A connection is already open"],
  ["Проверяем ранее работавший маршрут", "Checking the previously successful route"],
  ["Сохранённый маршрут устарел; запускаем новый поиск", "The saved route is stale; starting a new search"],
  [
    "PeerId найден не был или у него нет WSS/WebTransport/relay-адреса.",
    "The PeerId was not found or it has no WSS, WebTransport, or relay address.",
  ],
  [
    "Откройте дополнительные настройки и укажите relay multiaddr.",
    "Open the advanced settings and provide a relay multiaddr.",
  ],
  ["Таймаут подключения должен быть положительным целым числом", "The connection timeout must be a positive integer"],
  ["Сначала установите соединение", "Establish a connection first"],
  ["Запись в канал уже закрыта", "The channel write side is already closed"],
  ["Удалённая сторона завершила передачу", "The remote peer finished sending data"],
  ["Соединение закрыто пользователем", "The connection was closed by the user"],
  ["EOF отправлен; канал остаётся открытым для приёма", "EOF sent; the channel remains open for receiving"],
  ["Ошибка сетевого Web Worker", "Network Web Worker error"],
  ["Web Worker остановлен", "Web Worker stopped"],
  ["Клиент уже остановлен", "The client has already stopped"],
  ["Выбран указанный libp2p relay", "Selected the specified libp2p relay"],
  ["Приватный pairing-token режим включён", "Private pairing-token mode enabled"],
  ["Native WebRTC отключён без pairing token", "Native WebRTC is disabled without a pairing token"],
  ["WebRTC: pairing token защищает native signaling", "WebRTC: the pairing token protects native signaling"],
  ["Выбран libp2p IPFS-маршрут", "Selected the libp2p IPFS route"],
  ["Выбран собственный прямой WebRTC-канал", "Selected the native direct WebRTC channel"],
  ["Ни один транспорт не установил соединение:", "No transport established a connection:"],
  ["P2P-канал ещё не открыт", "The P2P channel is not open yet"],
  ["Ошибка PTY-протокола:", "PTY protocol error:"],
  ["WebRTC signaling вернул данные неизвестного типа", "WebRTC signaling returned data of an unknown type"],
  ["WebRTC не поддерживается этим браузером", "WebRTC is not supported by this browser"],
  ["WebSocket signaling не поддерживается этим браузером", "WebSocket signaling is not supported by this browser"],
  ["Некорректная подпись WebRTC PeerId", "Invalid WebRTC PeerId signature"],
  ["WebRTC handshake отклонён:", "WebRTC handshake rejected:"],
  ["WebRTC-канал восстановлен; продолжаем прежнюю сессию", "WebRTC channel restored; continuing the existing session"],
  ["WebRTC-подключение уже завершено", "The WebRTC connection attempt has already finished"],
  ["WebRTC-канал не открыт", "The WebRTC channel is not open"],
  ["WebRTC-подключение отменено", "The WebRTC connection was cancelled"],
  ["WebRTC-соединение закрыто пользователем", "The WebRTC connection was closed by the user"],
  ["Собственный WebRTC signaling не запущен:", "Native WebRTC signaling did not start:"],
  ["WebRTC: собственный поиск через Nostr и BitTorrent signaling", "WebRTC: native discovery through Nostr and BitTorrent signaling"],
  ["PeerId не указан", "PeerId is required"],
  ["Multiaddr не указан", "Multiaddr is required"],
  ["Браузеру нужен WebSocket relay-адрес с /ws или /wss", "The browser requires a WebSocket relay address with /ws or /wss"],
  ["HTTPS-страница может подключаться только к защищённому /wss relay", "An HTTPS page can connect only to a secure /wss relay"],
];

export function localizeDiagnostic(message: string, language: Language) {
  if (language === "ru") return message;
  let localized = message;
  for (const [pattern, replacement] of englishDiagnosticPatterns) {
    localized = localized.replace(pattern, replacement);
  }
  for (const [source, replacement] of englishDiagnosticFragments) {
    localized = localized.replaceAll(source, replacement);
  }
  return localized;
}
